import { useEffect, useMemo, useState } from "react";
import Layout from "@/components/Layout";
import { api, API, fmtBRL } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { useAuth } from "@/lib/auth";
import { Separator } from "@/components/ui/separator";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Calculator, Loader2, Plus, Trash2, Save, FileDown, BookOpen, Layers, BookCopy,
  Scissors, Hash, Settings2,
} from "lucide-react";
import { toast } from "sonner";

const PRECOS = {
  por_pagina: {
    revisao_orto: { label: "Revisão ortográfica", price: 3.0, unit: "/página" },
    revisao_critica: { label: "Revisão crítica", price: 5.0, unit: "/página" },
    leitura_sensivel: { label: "Leitura sensível", price: 2.0, unit: "/página" },
    leitura_critica: { label: "Leitura crítica", price: 2.0, unit: "/página" },
  },
  por_100_paginas: {
    diagramacao_ebook: { label: "Diagramação E-book", price: 300.0, unit: "a cada 100 págs" },
    diagramacao_fisico: { label: "Diagramação livro físico", price: 300.0, unit: "a cada 100 págs" },
  },
  fixos: {
    conversao_epub: { label: "Conversão EPUB", price: 300.0 },
    selo_editorial: { label: "Selo editorial", price: 500.0 },
    marketplaces: { label: "Inserção nos marketplaces", price: 100.0 },
    capa_ebook: { label: "Capa para E-book", price: 100.0 },
    capa_completa: { label: "Capa completa", price: 300.0 },
    fechamento_capa: { label: "Fechamento de capa", price: 180.0 },
    marcador: { label: "Arte marcador de páginas", price: 80.0 },
    booktrailer: { label: "Booktrailer", price: 300.0 },
    bienal: { label: "Bienal", price: 700.0 },
  },
  por_unidade: {
    arte_grafica: { label: "Arte gráfica", price: 100.0 },
    materia_nacional: { label: "Matéria em jornais e revistas (Nacional)", price: 150.0 },
    materia_intl: { label: "Matéria em jornais e revistas (Internacional)", price: 500.0 },
    criacao_artigo: { label: "Criação de artigo para jornal e revista", price: 150.0 },
  },
};

const TAMANHOS = ["140x210", "160x230", "170x240", "210x280", "210x297", "250x300"];
const CAPA_TIPOS = [
  { value: "cartao_triplex", label: "Cartão Triplex", gramaturas: ["250", "300"] },
  { value: "couche_brilho", label: "Couché brilho", gramaturas: ["250", "300"] },
  { value: "couche_fosco", label: "Couché fosco", gramaturas: ["250", "300"] },
];
const CORES_CAPA = ["1x0", "1x1", "4x0", "4x1", "4x4"];
const PAPEIS_MIOLO = [
  { value: "polen_soft", label: "Pólen Soft", gramaturas: ["80"] },
  { value: "off_set", label: "Offset", gramaturas: ["63", "75", "90", "120"] },
  { value: "couche_fosco", label: "Couché Fosco", gramaturas: ["115", "150"] },
  { value: "couche_brilho", label: "Couché Brilho", gramaturas: ["80", "115", "150"] },
];
const CORES_MIOLO = [
  { value: "1x1", label: "1x1 (P&B)" },
  { value: "4x4", label: "4x4 (Color)" },
  { value: "1x1e4x4", label: "1x1 + 4x4 (misto)" },
];
const LAMINACOES = ["Brilho", "Fosca", "Sem laminação"];
const ACABAMENTOS = [
  { value: "2", label: "Fresado PUR" },
  { value: "4", label: "Grampo" },
  { value: "5", label: "Espiral" },
  { value: "6", label: "Wire-O" },
];

const initialPrint = {
  rd_tamanho: "140x210", tamanho: "140x210",
  tipo_de_capa: "couche_fosco", gramatura_capa: "250", cores_da_capa: "4x0", laminacao_da_capa: "Brilho",
  orelha: "Não", orelha_centimetros: "0", marcador: "Não", marcador_centimetros: "0",
  tipo_de_papel: "polen_soft", gramatura_miolo: "80", cores_do_miolo: "1x1",
  quantidade_de_paginas1x1: "100", quantidade_de_paginas4x4: "0",
  tipo_de_acabamento: "2", embalagem_individual: "Não",
  tiragens: "50",
};

export default function Orcamento() {
  // Header info (shared)
  const [info, setInfo] = useState({ titulo: "", isbn: "", paginas: 100, agente: "", observacao: "" });
  const [includeEditoracao, setIncludeEditoracao] = useState(true);
  const [includeImpressao, setIncludeImpressao] = useState(false);

  // Editorial selections
  const [edFlags, setEdFlags] = useState({});       // per_pagina + por_100 + fixos as bool
  const [edQtd, setEdQtd] = useState({});           // por_unidade qty
  const [frete, setFrete] = useState(0);
  const [outros, setOutros] = useState([]);

  // Print form
  const [print_, setPrint] = useState(initialPrint);

  // Results
  const [edResult, setEdResult] = useState(null);
  const [prResult, setPrResult] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const { user: currentUser } = useAuth();
  const isVendor = currentUser?.role === "vendedor";
  const [agents, setAgents] = useState([]);

  useEffect(() => {
    api.get("/prestadores?all=true")
      .then(r => setAgents(r.data || []))
      .catch(() => setAgents([]));
  }, []);

  // Vendedor: agente automaticamente é ele mesmo
  useEffect(() => {
    if (isVendor && currentUser?.name && !info.agente) {
      setInfo(prev => ({ ...prev, agente: currentUser.name }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVendor, currentUser?.name]);

  const totalEditoracao = edResult?.total || 0;
  const tiragensList = useMemo(() => {
    if (!prResult) return [];
    return prResult.items.map(it => ({ tiragem: it.tiragem, total: it.preco_total, unit: it.preco_unit }));
  }, [prResult]);

  const totalGeral = totalEditoracao + (tiragensList[0]?.total || 0); // by 1st tiragem if multiple

  // Build label list of editorial services for PDF / save
  const buildEditorialLabels = () => {
    const labels = [];
    Object.entries(PRECOS.por_pagina).forEach(([k, v]) => { if (edFlags[k]) labels.push(`${v.label} (${info.paginas} págs)`); });
    Object.entries(PRECOS.por_100_paginas).forEach(([k, v]) => { if (edFlags[k]) labels.push(`${v.label}`); });
    Object.entries(PRECOS.fixos).forEach(([k, v]) => { if (edFlags[k]) labels.push(v.label); });
    Object.entries(PRECOS.por_unidade).forEach(([k, v]) => {
      const q = Number(edQtd[k] || 0);
      if (q > 0) labels.push(`${v.label} (${q}x)`);
    });
    if (Number(frete) > 0) labels.push("Frete (incluso no total)");
    outros.filter(o => o.descricao && Number(o.valor) > 0).forEach(o => labels.push(`${o.descricao}`));
    return labels;
  };

  const calcular = async () => {
    if (!includeEditoracao && !includeImpressao) return toast.error("Selecione ao menos Editoração ou Impressão");
    setCalculating(true);
    try {
      const tasks = [];
      if (includeEditoracao) {
        const payload = {
          paginas: Number(info.paginas) || 0,
          por_pagina: Object.fromEntries(Object.keys(PRECOS.por_pagina).map(k => [k, !!edFlags[k]])),
          por_100_paginas: Object.fromEntries(Object.keys(PRECOS.por_100_paginas).map(k => [k, !!edFlags[k]])),
          fixos: Object.fromEntries(Object.keys(PRECOS.fixos).map(k => [k, !!edFlags[k]])),
          por_unidade_qtd: Object.fromEntries(Object.entries(edQtd).map(([k, v]) => [k, Number(v) || 0])),
          frete: Number(frete) || 0,
          outros: outros.filter(o => o.descricao && Number(o.valor) > 0).map(o => ({ descricao: o.descricao, valor: Number(o.valor) })),
        };
        tasks.push(api.post("/orcamentos/editorial/calc", payload).then(r => setEdResult(r.data)));
      } else setEdResult(null);
      if (includeImpressao) {
        const pags = Number(print_.quantidade_de_paginas1x1 || 0) + Number(print_.quantidade_de_paginas4x4 || 0);
        if (pags <= 0) throw new Error("Informe as páginas P&B e/ou Color em Impressão");
        const tirs = (print_.tiragens || "").split(",").map(s => s.trim()).filter(Boolean);
        if (tirs.length === 0) throw new Error("Informe ao menos uma tiragem");
        const payload = {
          ...print_,
          titulo_livro: info.titulo, isbn: info.isbn,
          quantidade_de_paginas: String(pags),
          quantidade_de_tiragens: tirs[0],
        };
        tasks.push(api.post("/orcamentos/imprimir/calc", payload).then(r => setPrResult(r.data)));
      } else setPrResult(null);
      await Promise.all(tasks);
      toast.success("Orçamento calculado");
    } catch (e) {
      toast.error(e.message || e.response?.data?.detail || "Erro ao calcular");
    } finally { setCalculating(false); }
  };

  const buildSavePayload = () => ({
    titulo: info.titulo, isbn: info.isbn, paginas: Number(info.paginas) || 0,
    agente: info.agente, observacao: info.observacao,
    include_editoracao: includeEditoracao, include_impressao: includeImpressao,
    total_editoracao: totalEditoracao,
    total_impressao_por_tiragem: tiragensList.map(t => ({ tiragem: t.tiragem, total: t.total })),
    editorial_services: buildEditorialLabels(),
    printing_payload: includeImpressao ? print_ : null,
    editorial_payload: includeEditoracao ? { flags: edFlags, qtd: edQtd, frete, outros } : null,
  });

  const salvar = async () => {
    if (!edResult && !prResult) return toast.error("Calcule o orçamento antes de salvar");
    setSaving(true);
    try {
      await api.post("/orcamentos/save", buildSavePayload());
      toast.success("Orçamento salvo");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao salvar"); }
    finally { setSaving(false); }
  };

  const gerarPdf = async () => {
    if (!edResult && !prResult) return toast.error("Calcule o orçamento antes de gerar o PDF");
    if (!info.agente) return toast.error("Informe o(a) Agente Literário(a)");
    setGenerating(true);
    try {
      const resp = await fetch(`${API}/orcamentos/pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` },
        credentials: "include",
        body: JSON.stringify({ ...buildSavePayload(), printing_payload: includeImpressao ? print_ : null }),
      });
      if (!resp.ok) throw new Error("Erro ao gerar PDF");
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      const cd = resp.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      a.download = m ? m[1] : "orcamento.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF gerado");
    } catch (e) { toast.error(e.message || "Erro ao gerar PDF"); }
    finally { setGenerating(false); }
  };

  const capaTipo = useMemo(() => CAPA_TIPOS.find(c => c.value === print_.tipo_de_capa) || CAPA_TIPOS[0], [print_.tipo_de_capa]);
  const papelMiolo = useMemo(() => PAPEIS_MIOLO.find(p => p.value === print_.tipo_de_papel) || PAPEIS_MIOLO[0], [print_.tipo_de_papel]);
  useEffect(() => {
    if (!capaTipo.gramaturas.includes(print_.gramatura_capa)) setPrint(p => ({ ...p, gramatura_capa: capaTipo.gramaturas[0] }));
  }, [print_.tipo_de_capa, capaTipo, print_.gramatura_capa]);
  useEffect(() => {
    if (!papelMiolo.gramaturas.includes(print_.gramatura_miolo)) setPrint(p => ({ ...p, gramatura_miolo: papelMiolo.gramaturas[0] }));
  }, [print_.tipo_de_papel, papelMiolo, print_.gramatura_miolo]);

  return (
    <Layout title="Orçamentos" subtitle="Editoração e impressão consolidados">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* ===== Shared header ===== */}
          <Card className="rounded-sm p-5 space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div className="sm:col-span-2">
                <Label className="text-xs font-semibold">Título da obra</Label>
                <Input data-testid="orc-titulo" value={info.titulo} onChange={(e) => setInfo({ ...info, titulo: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold">ISBN</Label>
                <Input data-testid="orc-isbn" value={info.isbn} onChange={(e) => setInfo({ ...info, isbn: e.target.value })} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs font-semibold">Quantidade de páginas</Label>
                <Input data-testid="orc-paginas" type="number" min="0" value={info.paginas} onChange={(e) => setInfo({ ...info, paginas: e.target.value })} className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-semibold">Agente Literário(a) responsável</Label>
                {isVendor ? (
                  <Input
                    data-testid="orc-agente"
                    value={info.agente || (currentUser?.name || "")}
                    disabled
                    className="mt-1 bg-muted"
                    readOnly
                  />
                ) : (
                  <SearchableSelect
                    value={info.agente}
                    onValueChange={(v) => setInfo({ ...info, agente: v })}
                    options={agents.map(a => ({
                      value: a.name,
                      label: a.name,
                      hint: `${a.role || ""}${a.username ? " · @" + a.username : ""}`,
                    }))}
                    placeholder="Selecionar..."
                    searchPlaceholder="Buscar por nome, usuário ou cargo..."
                    emptyText="Nenhum usuário ativo encontrado."
                    testid="orc-agente"
                    className="mt-1"
                  />
                )}
              </div>
              <div>
                <Label className="text-xs font-semibold">Observações (opcional, aparece no PDF)</Label>
                <Textarea rows={2} value={info.observacao} onChange={(e) => setInfo({ ...info, observacao: e.target.value })} className="mt-1" data-testid="orc-obs" />
              </div>
            </div>

            <Separator />

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="flex items-start gap-3 p-3 rounded-sm border border-border cursor-pointer hover:bg-secondary/40">
                <Switch checked={includeEditoracao} onCheckedChange={setIncludeEditoracao} data-testid="orc-toggle-editoracao" />
                <div>
                  <div className="text-sm font-bold">Incluir Editoração</div>
                  <div className="text-[11px] text-muted-foreground">Revisões, diagramação, capa, marketing etc.</div>
                </div>
              </label>
              <label className="flex items-start gap-3 p-3 rounded-sm border border-border cursor-pointer hover:bg-secondary/40">
                <Switch checked={includeImpressao} onCheckedChange={setIncludeImpressao} data-testid="orc-toggle-impressao" />
                <div>
                  <div className="text-sm font-bold">Incluir Impressão</div>
                  <div className="text-[11px] text-muted-foreground">Cotação em tempo real via fornecedor.</div>
                </div>
              </label>
            </div>
          </Card>

          {/* ===== Editoração ===== */}
          {includeEditoracao && (
            <Card className="rounded-sm p-5 space-y-5">
              <SectionHeader icon={<BookOpen className="w-4 h-4" />} title="Editoração" />

              <Group title="Serviços por página" hint="Valor automaticamente multiplicado pelo nº de páginas">
                <CheckboxRows
                  items={PRECOS.por_pagina}
                  flags={edFlags} setFlags={setEdFlags}
                  prefix="ed-pp"
                />
              </Group>

              <Group title="Serviços a cada 100 páginas" hint="Cada bloco de 100 páginas, arredondado para cima">
                <CheckboxRows items={PRECOS.por_100_paginas} flags={edFlags} setFlags={setEdFlags} prefix="ed-p100" />
              </Group>

              <Group title="Serviços com valor fixo" hint="Independente do nº de páginas">
                <CheckboxRows items={PRECOS.fixos} flags={edFlags} setFlags={setEdFlags} prefix="ed-fx" />
              </Group>

              <Group title="Serviços por unidade" hint="Informe a quantidade para cada um">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {Object.entries(PRECOS.por_unidade).map(([k, v]) => (
                    <div key={k} className="flex items-center gap-2 px-2 py-2 rounded-sm bg-secondary/30">
                      <span className="text-xs flex-1">{v.label} <span className="text-muted-foreground">({fmtBRL(v.price)}/un.)</span></span>
                      <Input
                        data-testid={`ed-u-${k}`}
                        type="number" min="0" placeholder="qtd" className="w-20"
                        value={edQtd[k] || ""} onChange={(e) => setEdQtd(q => ({ ...q, [k]: e.target.value }))}
                      />
                    </div>
                  ))}
                </div>
              </Group>

              <Group title="Frete e outros serviços">
                <div className="space-y-2">
                  <div className="flex items-center gap-2 px-2 py-2 rounded-sm bg-secondary/30">
                    <span className="text-xs flex-1">Frete</span>
                    <Input data-testid="ed-frete" placeholder="R$" type="number" min="0" step="0.01" className="w-32" value={frete} onChange={(e) => setFrete(e.target.value)} />
                  </div>
                  {outros.map((o, i) => (
                    <div key={i} className="flex items-center gap-2 px-2 py-2 rounded-sm bg-secondary/30">
                      <Input placeholder="Descrição do serviço" className="flex-1" value={o.descricao} onChange={(e) => setOutros(arr => arr.map((x, j) => j === i ? { ...x, descricao: e.target.value } : x))} />
                      <Input placeholder="R$" type="number" min="0" step="0.01" className="w-28" value={o.valor} onChange={(e) => setOutros(arr => arr.map((x, j) => j === i ? { ...x, valor: e.target.value } : x))} />
                      <Button variant="ghost" size="sm" onClick={() => setOutros(arr => arr.filter((_, j) => j !== i))}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  ))}
                  <Button data-testid="ed-add-outro" variant="outline" size="sm" onClick={() => setOutros(arr => [...arr, { descricao: "", valor: 0 }])}>
                    <Plus className="w-3.5 h-3.5 mr-1" />Adicionar outro serviço
                  </Button>
                </div>
              </Group>
            </Card>
          )}

          {/* ===== Impressão ===== */}
          {includeImpressao && (
            <Card className="rounded-sm p-5 space-y-5">
              <SectionHeader icon={<BookCopy className="w-4 h-4" />} title="Impressão" />

              <Group title="Formato" icon={<Hash className="w-3.5 h-3.5" />}>
                <Pills options={[...TAMANHOS, "personalizado"]} value={print_.rd_tamanho} onChange={(v) => setPrint({ ...print_, rd_tamanho: v, tamanho: v === "personalizado" ? (print_.tamanho_custom || "") : v })} testid="orc-tamanho" />
                {print_.rd_tamanho === "personalizado" && (
                  <div className="mt-2 flex items-center gap-2">
                    <Label className="text-xs whitespace-nowrap">Formato personalizado (mm)</Label>
                    <Input
                      data-testid="orc-tamanho-custom"
                      placeholder="ex.: 148x210"
                      value={print_.tamanho_custom || ""}
                      onChange={(e) => setPrint({ ...print_, tamanho_custom: e.target.value, tamanho: e.target.value })}
                      className="w-40 h-8 text-sm"
                    />
                  </div>
                )}
                <p className="text-[10px] text-muted-foreground mt-2">Dimensões em milímetros (mm). Use &quot;LARGURAxALTURA&quot; (ex.: 148x210).</p>
              </Group>

              <Group title="Capa" icon={<Layers className="w-3.5 h-3.5" />}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Tipo de capa"><Pills options={CAPA_TIPOS.map(c => ({ value: c.value, label: c.label }))} value={print_.tipo_de_capa} onChange={(v) => setPrint(p => ({ ...p, tipo_de_capa: v }))} /></Field>
                  <Field label="Gramatura"><Pills options={capaTipo.gramaturas.map(g => ({ value: g, label: `${g}g/m²` }))} value={print_.gramatura_capa} onChange={(v) => setPrint(p => ({ ...p, gramatura_capa: v }))} /></Field>
                  <Field label="Cores"><Pills options={CORES_CAPA} value={print_.cores_da_capa} onChange={(v) => setPrint(p => ({ ...p, cores_da_capa: v }))} /></Field>
                  <Field label="Laminação"><Pills options={LAMINACOES} value={print_.laminacao_da_capa} onChange={(v) => setPrint(p => ({ ...p, laminacao_da_capa: v }))} /></Field>
                  <Field label="Orelhas">
                    <Pills options={["Sim", "Não"]} value={print_.orelha} onChange={(v) => setPrint(p => ({ ...p, orelha: v }))} />
                    {print_.orelha === "Sim" && <Input type="number" min="0" placeholder="cm" className="mt-2" value={print_.orelha_centimetros} onChange={(e) => setPrint(p => ({ ...p, orelha_centimetros: e.target.value }))} />}
                  </Field>
                  <Field label="Marcador">
                    <Pills options={["Sim", "Não"]} value={print_.marcador} onChange={(v) => setPrint(p => ({ ...p, marcador: v }))} />
                    {print_.marcador === "Sim" && <Input type="number" min="0" placeholder="cm" className="mt-2" value={print_.marcador_centimetros} onChange={(e) => setPrint(p => ({ ...p, marcador_centimetros: e.target.value }))} />}
                  </Field>
                </div>
              </Group>

              <Group title="Miolo" icon={<BookOpen className="w-3.5 h-3.5" />}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Tipo de papel"><Pills options={PAPEIS_MIOLO.map(p => ({ value: p.value, label: p.label }))} value={print_.tipo_de_papel} onChange={(v) => setPrint(p => ({ ...p, tipo_de_papel: v }))} /></Field>
                  <Field label="Gramatura"><Pills options={papelMiolo.gramaturas.map(g => ({ value: g, label: `${g}g/m²` }))} value={print_.gramatura_miolo} onChange={(v) => setPrint(p => ({ ...p, gramatura_miolo: v }))} /></Field>
                  <Field label="Cores"><Pills options={CORES_MIOLO} value={print_.cores_do_miolo} onChange={(v) => setPrint(p => ({ ...p, cores_do_miolo: v }))} /></Field>
                  <div />
                  <Field label="Páginas P&B (1x1)"><Input data-testid="orc-pag-1x1" type="number" min="0" value={print_.quantidade_de_paginas1x1} onChange={(e) => setPrint(p => ({ ...p, quantidade_de_paginas1x1: e.target.value }))} /></Field>
                  <Field label="Páginas Color (4x4)"><Input data-testid="orc-pag-4x4" type="number" min="0" value={print_.quantidade_de_paginas4x4} onChange={(e) => setPrint(p => ({ ...p, quantidade_de_paginas4x4: e.target.value }))} /></Field>
                </div>
              </Group>

              <Group title="Acabamento" icon={<Scissors className="w-3.5 h-3.5" />}>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Field label="Tipo"><Pills options={ACABAMENTOS} value={print_.tipo_de_acabamento} onChange={(v) => setPrint(p => ({ ...p, tipo_de_acabamento: v }))} /></Field>
                  <Field label="Embalagem individual (shrink)"><Pills options={["Sim", "Não"]} value={print_.embalagem_individual} onChange={(v) => setPrint(p => ({ ...p, embalagem_individual: v }))} /></Field>
                </div>
              </Group>

              <Group title="Tiragem" icon={<Settings2 className="w-3.5 h-3.5" />} hint="Quantidade de exemplares a ser impressa">
                <Input data-testid="orc-tiragens" type="number" min="1" value={print_.tiragens} onChange={(e) => setPrint(p => ({ ...p, tiragens: e.target.value }))} placeholder="ex.: 50" className="w-40" />
              </Group>
            </Card>
          )}

          <div className="flex flex-wrap gap-2 justify-end">
            <Button data-testid="orc-calcular-btn" onClick={calcular} disabled={calculating} size="lg">
              {calculating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Calculator className="w-4 h-4 mr-2" />}
              Calcular Orçamento
            </Button>
            <Button data-testid="orc-salvar-btn" onClick={salvar} disabled={saving || (!edResult && !prResult)} variant="outline" size="lg">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Salvar orçamento
            </Button>
            <Button data-testid="orc-pdf-btn" onClick={gerarPdf} disabled={generating || (!edResult && !prResult)} size="lg">
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
              Gerar PDF
            </Button>
          </div>
        </div>

        {/* ===== Summary side ===== */}
        <Card className="rounded-sm p-5 space-y-4 h-fit lg:sticky lg:top-4">
          <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Resumo</div>
          <div className="text-xs space-y-1.5">
            <Row k="Título" v={info.titulo} />
            <Row k="ISBN" v={info.isbn} />
            <Row k="Páginas" v={info.paginas} />
            <Row k="Agente Literário(a)" v={info.agente} />
          </div>

          <Separator />

          {includeEditoracao && (
            <div className="space-y-1">
              <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Editoração</div>
              {edResult ? (
                <div className="flex justify-between items-baseline">
                  <span className="text-xs text-muted-foreground">Total</span>
                  <span className="text-xl font-bold text-[hsl(var(--primary))]" data-testid="resumo-total-ed">{fmtBRL(edResult.total)}</span>
                </div>
              ) : (
                <p className="text-[11px] text-muted-foreground">Selecione os serviços e clique em <b>Calcular</b>.</p>
              )}
            </div>
          )}

          {includeImpressao && (
            <div className="space-y-2">
              <div className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">Impressão</div>
              {prResult && tiragensList.length > 0 ? (
                <>
                  <Row k="Tiragem" v={`${tiragensList[0].tiragem} exemplares`} />
                  <Row k="Preço unitário" v={fmtBRL(tiragensList[0].unit)} />
                  <div className="flex justify-between items-baseline">
                    <span className="text-xs text-muted-foreground">Total</span>
                    <span className="text-xl font-bold text-[hsl(var(--primary))]" data-testid="resumo-total-impr">{fmtBRL(tiragensList[0].total)}</span>
                  </div>
                </>
              ) : (
                <p className="text-[11px] text-muted-foreground">Preencha as características e clique em <b>Calcular</b>.</p>
              )}
            </div>
          )}

          {((edResult && includeEditoracao) || (prResult && includeImpressao)) && (
            <>
              <Separator />
              <div className="rounded-sm p-3 bg-[hsl(var(--primary)/0.08)] border border-[hsl(var(--primary)/0.4)]">
                <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">Total Geral</div>
                <div className="text-2xl font-bold text-[hsl(var(--primary))] mt-1" data-testid="resumo-total-geral">{fmtBRL(totalGeral)}</div>
                <div className="text-[10px] text-muted-foreground mt-1">
                  {includeEditoracao && <span>Editoração {fmtBRL(totalEditoracao)}</span>}
                  {includeEditoracao && includeImpressao && tiragensList[0] && <span> + Impressão {fmtBRL(tiragensList[0]?.total || 0)}</span>}
                  {!includeEditoracao && includeImpressao && tiragensList[0] && <span>Impressão de {tiragensList[0].tiragem} exemplares</span>}
                </div>
              </div>
            </>
          )}
        </Card>
      </div>
    </Layout>
  );
}

/* ============= Helpers ============= */
function SectionHeader({ icon, title }) {
  return (
    <div className="flex items-center gap-2 pb-2 border-b border-border">
      <span className="text-[hsl(var(--primary))]">{icon}</span>
      <span className="text-sm font-bold uppercase tracking-wider">{title}</span>
    </div>
  );
}
function Group({ title, hint, icon, children }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        {icon && <span className="text-muted-foreground">{icon}</span>}
        <span className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{title}</span>
      </div>
      {hint && <p className="text-[10px] text-muted-foreground mb-2">{hint}</p>}
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return (<div><Label className="text-xs font-semibold">{label}</Label><div className="mt-1">{children}</div></div>);
}
function Row({ k, v }) {
  return (<div className="flex justify-between gap-3"><span className="text-muted-foreground">{k}</span><span className="font-medium text-right">{v || "—"}</span></div>);
}
function CheckboxRows({ items, flags, setFlags, prefix }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
      {Object.entries(items).map(([k, v]) => (
        <label key={k} className={`flex items-center gap-2 px-2 py-2 rounded-sm cursor-pointer transition ${flags[k] ? "bg-[hsl(var(--primary)/0.1)] border border-[hsl(var(--primary)/0.4)]" : "bg-secondary/30 border border-transparent hover:bg-secondary/50"}`}>
          <Checkbox
            data-testid={`${prefix}-${k}`}
            checked={!!flags[k]}
            onCheckedChange={(c) => setFlags(f => ({ ...f, [k]: !!c }))}
          />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium truncate">{v.label}</div>
            <div className="text-[10px] text-muted-foreground">{fmtBRL(v.price)} {v.unit || ""}</div>
          </div>
        </label>
      ))}
    </div>
  );
}
function Pills({ options, value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {options.map((o) => {
        const val = typeof o === "string" ? o : o.value;
        const lab = typeof o === "string" ? o : o.label;
        const active = value === val;
        return (
          <button
            key={val}
            data-testid={testid ? `${testid}-${val}` : undefined}
            type="button"
            onClick={() => onChange(val)}
            className={`text-xs px-3 py-1 rounded-full border transition ${active ? "bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] border-[hsl(var(--primary))]" : "bg-card border-border hover:border-[hsl(var(--primary))] hover:bg-secondary/40"}`}
          >
            {lab}
          </button>
        );
      })}
    </div>
  );
}
